// @ts-nocheck
import { OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE } from '../constants';
import { IAssistantMessage, IQuestion, IQuestionOption } from '../types';
import { getAnswersFromMessages } from './displayCondition';

/**
 * Resolve the correct price for a window type / variation option given the
 * current answers. Checks the option's priceList from the original config;
 * if a rule matches, returns its price. Otherwise returns the config default.
 *
 * A matched priceList entry is this option's own SEPARATE UNGLAZED base price
 * (the priceList condition is keyed off the "Unglazed" glazing selection) —
 * it is not just a discount applied to the glazed price. Discount priority:
 *   1. unglazedDiscountPercentage/Amount, if the config defines one
 *   2. else priceListDiscounts[matchIndex] — looked up by the matched entry's
 *      OWN index into THIS SAME option's own priceList/priceListDiscounts
 *      arrays, so a discount authored for a different product's row can't
 *      leak onto this one via a coincidental index match (the original bug
 *      this exclusion was guarding against).
 *   3. else the option's own discountPercentage/discountAmount (the account/
 *      option-level discount) — same final fallback as applyMargin.ts's
 *      transformedPriceList, so the unglazed VSS discount is computed from
 *      the unglazed base rather than silently dropping to the stale glazed rate.
 * When nothing matched (still on the glazed price), the option's generic
 * discountPercentage/discountAmount apply as normal.
 *
 * This priority chain must stay identical to the one in applyMargin.ts's
 * transformedPriceList (which bakes the discount into priceList[i].price at
 * config-transform time) — otherwise the discount shown here for the
 * breakdown display would disagree with what's actually reflected in price.
 *
 * Without step 1, switching glazing re-resolves price/vssPrice to a
 * different priceList entry but keeps whatever discount% happened to be on
 * the option before, which can belong to a completely different (previously
 * matched) entry — e.g. price correctly updates to £296 but the discount
 * still reflects a stale £311 entry's rate, producing a discount amount that
 * doesn't match the displayed gross price.
 */
const resolveGlazingPrice = (
    option: IQuestionOption,
    flowOpt: IQuestionOption | undefined,
    answers: Record<string, IQuestionOption[]>,
): { price: number; vssPrice: number; discountPercentage?: number; discountAmount?: number } => {
    const priceList = flowOpt?.priceList ?? option.priceList;
    const defaultPrice = flowOpt?.price ?? option.price;
    const defaultVssPrice = flowOpt?.vssPrice ?? option.vssPrice ?? 0;
    const defaultDiscountPercentage = flowOpt?.discountPercentage ?? option.discountPercentage;
    const defaultDiscountAmount = flowOpt?.discountAmount ?? option.discountAmount;
    const unglazedDiscountPercentage = flowOpt?.unglazedDiscountPercentage ?? option.unglazedDiscountPercentage;
    const unglazedDiscountAmount = flowOpt?.unglazedDiscountAmount ?? option.unglazedDiscountAmount;
    const priceListDiscounts = flowOpt?.priceListDiscounts ?? option.priceListDiscounts;

    if (!priceList?.length) {
        return {
            price: defaultPrice,
            vssPrice: defaultVssPrice,
            discountPercentage: defaultDiscountPercentage,
            discountAmount: defaultDiscountAmount,
        };
    }

    let match: any = null;
    let matchIndex = -1;
    priceList.forEach((item: any, idx: number) => {
        if (!item.conditions?.length) return;
        const allMatch = item.conditions.every((cond: any) => {
            if (!cond.categoryId || !cond.optionIds?.length) return false;
            const selected = answers[cond.categoryId];
            if (!selected?.length) return false;
            const selectedIds = selected.map((o: IQuestionOption) => o.optionId);
            return cond.optionIds.some((id: string) => selectedIds.includes(id));
        });
        if (!allMatch) return;
        if (!match) { match = item; matchIndex = idx; return; }
        if ((item.conditions?.length ?? 0) >= (match.conditions?.length ?? 0)) { match = item; matchIndex = idx; }
    });

    // Same priority chain used to bake the discount into priceList[i].price
    // in applyMargin.ts's transformedPriceList — must stay in sync, or the
    // breakdown's displayed discount% won't match what's actually reflected
    // in the price. Unglazed fields (when defined) win outright — they're the
    // only purpose-built override for this scenario; else fall back to
    // priceListDiscounts[matchIndex], scoped to this option's own arrays.
    const unglazedDiscountDefined = (unglazedDiscountPercentage ?? 0) > 0 || (unglazedDiscountAmount ?? 0) > 0;
    const plDiscount = match ? priceListDiscounts?.find((d: any) => d.index === matchIndex) : undefined;

    return {
        price: match?.price ?? defaultPrice,
        vssPrice: match?.vssPrice ?? defaultVssPrice,
        discountPercentage: !match
            ? defaultDiscountPercentage
            : unglazedDiscountDefined
                ? unglazedDiscountPercentage
                : plDiscount
                    ? plDiscount.discountPercentage ?? 0
                    : defaultDiscountPercentage,
        discountAmount: !match
            ? defaultDiscountAmount
            : unglazedDiscountDefined
                ? unglazedDiscountAmount
                : plDiscount
                    ? plDiscount.discountAmount ?? 0
                    : defaultDiscountAmount,
    };
};

/**
 * When glazing type is selected, update the stored price on window type and
 * variation answer options in both bot (assistant) and user messages so that
 * price calculation and display reflect the glazing-dependent price.
 *
 * Base prices always come from questionFlow (original config) so that
 * switching glazing options is idempotent — it never builds on a previously
 * resolved value.
 */
export const applyGlazingPriceToMessages = (
    messages: IAssistantMessage[],
    questionFlow: IQuestion[],
    prebuiltAnswers?: Record<string, IQuestionOption[]>,
): IAssistantMessage[] => {
    // Use prebuiltAnswers when provided (e.g. from getMergedAnswers) so that
    // duplicate answers from merged original+draft message lists don't cause
    // the wrong priceList rule to match.
    const answers = prebuiltAnswers ?? getAnswersFromMessages(messages);

    // Build optionId → config option map so base prices always start from config defaults
    const flowOptionMap = new Map<string, IQuestionOption>();
    for (const q of questionFlow) {
        for (const opt of q.options ?? []) {
            if (opt.optionId) flowOptionMap.set(opt.optionId, opt);
        }
    }

    return messages.map(msg => {
        if (!msg.response?.answer?.length) return msg;

        const hasWindowOrVariation = msg.response.answer.some(
            a => a.optionType === OPTION_TYPE_WINDOW_TYPE || a.optionType === OPTION_TYPE_VARIATION
        );
        if (!hasWindowOrVariation) return msg;

        // Update response.answer — used by price calculation
        const updatedAnswer = msg.response.answer.map(a => {
            if (a.optionType !== OPTION_TYPE_WINDOW_TYPE && a.optionType !== OPTION_TYPE_VARIATION) {
                return a;
            }
            const flowOpt = flowOptionMap.get(a.optionId);
            const { price: resolvedPrice, vssPrice: resolvedVssPrice, discountPercentage, discountAmount } =
                resolveGlazingPrice(a, flowOpt, answers);
            // Clear priceModifier so the price calculation uses `price` only
            return { ...a, price: resolvedPrice, priceModifier: 0, vssPrice: resolvedVssPrice, discountPercentage, discountAmount };
        });

        // Update question.options — used by rendered option cards so displayed prices reflect glazing.
        // Clear priceModifier so cards don't show a second "+£X" line from the priceList resolution.
        const updatedOptions = (msg.question?.options ?? []).map(opt => {
            if (opt.optionType !== OPTION_TYPE_WINDOW_TYPE && opt.optionType !== OPTION_TYPE_VARIATION) {
                return opt;
            }
            const flowOpt = flowOptionMap.get(opt.optionId);
            const { price: resolvedPrice, vssPrice: resolvedVssPrice, discountPercentage, discountAmount } =
                resolveGlazingPrice(opt, flowOpt, answers);
            return { ...opt, price: resolvedPrice, priceModifier: 0, vssPrice: resolvedVssPrice, discountPercentage, discountAmount };
        });

        return {
            ...msg,
            question: { ...msg.question, options: updatedOptions },
            response: { ...msg.response, answer: updatedAnswer },
        };
    });
};
